import { StarFilled, StarOutlined } from '@ant-design/icons';
import { Button, message, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import {
  followOpportunity,
  unfollowOpportunity,
} from '@/services/bff/opportunity.api';
import type { OpportunityViewMode } from '@/services/bff/types/opportunity';

const FOLLOW_CHANGED_EVENT = 'crm:opportunity-follow-changed';

type OpportunityFollowButtonProps = {
  opportunityUuid?: string;
  isFollowed?: boolean;
  viewMode: OpportunityViewMode;
  onChange?: (isFollowed: boolean) => void;
};

export function OpportunityFollowButton({
  opportunityUuid,
  isFollowed = false,
  viewMode,
  onChange,
}: OpportunityFollowButtonProps) {
  const [followed, setFollowed] = useState(isFollowed);
  const [loading, setLoading] = useState(false);

  useEffect(() => setFollowed(Boolean(isFollowed)), [isFollowed]);

  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (
        event as CustomEvent<{ uuid: string; isFollowed: boolean }>
      ).detail;
      if (detail?.uuid === opportunityUuid) {
        setFollowed(detail.isFollowed);
        onChange?.(detail.isFollowed);
      }
    };
    window.addEventListener(FOLLOW_CHANGED_EVENT, listener);
    return () => window.removeEventListener(FOLLOW_CHANGED_EVENT, listener);
  }, [opportunityUuid, onChange]);

  const toggle = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const uuid = String(opportunityUuid || '').trim();
    if (!uuid || loading) return;
    const next = !followed;
    setFollowed(next);
    setLoading(true);
    try {
      if (next) await followOpportunity(uuid, viewMode);
      else await unfollowOpportunity(uuid);
      window.dispatchEvent(
        new CustomEvent(FOLLOW_CHANGED_EVENT, {
          detail: { uuid, isFollowed: next },
        }),
      );
      message.success(next ? '已关注该商机' : '已取消关注');
    } catch {
      setFollowed(!next);
      message.error(next ? '关注失败，请稍后重试' : '取消关注失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title={followed ? '取消关注' : '关注商机'}>
      <Button
        type="text"
        size="small"
        loading={loading}
        aria-label={followed ? '取消关注' : '关注商机'}
        icon={
          followed ? (
            <StarFilled style={{ color: '#faad14' }} />
          ) : (
            <StarOutlined />
          )
        }
        onClick={toggle}
      />
    </Tooltip>
  );
}
